import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { tokens } from '@/theme'
import { ErrorPanel, SkeletonBar } from '@/components/feedback'
import {
  useNotificationAction,
  useNotifications,
  useUnreadNotifications,
} from '@/api/hooks/notifications'
import { formatCompactAge } from '@/domain/relativeTime'
import type { AppNotification } from '@/domain/types'
import { useDismiss } from './useDismiss'

interface Props {
  /** Bumped by the shell on every route change, so the dot follows what happened elsewhere. */
  refreshKey: number
  onNavigate: (path: string) => void
  /** The phone header is 44px icons and the list spans the screen. */
  compact?: boolean
}

/** The bell, a gold dot while anything is unread, and the list it opens. */
export function NotificationsBell({ refreshKey, onNavigate, compact = false }: Props) {
  const [open, setOpen] = useState(false)
  const [readKey, setReadKey] = useState(0)
  const [listKey, setListKey] = useState(0)
  const unread = useUnreadNotifications(refreshKey + readKey)
  const hasUnread = unread.status === 'success' && unread.data.count > 0
  const { run: markRead } = useNotificationAction()
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  // Marked read once the list has loaded, not on the click: marking first
  // would make the list it is about to show arrive with nothing unread.
  const onSeen = useCallback(async () => {
    const done = await markRead('/api/notifications/read', 'POST')
    if (done !== null) setReadKey(k => k + 1)
  }, [markRead])

  // Clearing hides everything up to now for good; the list and the dot refetch.
  const onClear = useCallback(async () => {
    const done = await markRead('/api/notifications/clear', 'POST')
    if (done === null) return
    setListKey(k => k + 1)
    setReadKey(k => k + 1)
  }, [markRead])

  const size = compact ? 44 : 34
  return (
    <div ref={ref} style={{ position: compact ? 'static' : 'relative' }}>
      <button
        type="button"
        aria-label={hasUnread ? 'Notifications, unread' : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          width: size,
          height: size,
          background: open ? tokens.inkPanel2 : 'transparent',
          border: 'none',
          borderRadius: tokens.radiusMd,
          color: open ? tokens.paper : tokens.inkPanelText,
          cursor: 'pointer',
        }}
      >
        <Bell size={compact ? 18 : 15} strokeWidth={1.6} />
        {hasUnread && (
          <span
            data-testid="unread-dot"
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: compact ? 11 : 7,
              right: compact ? 11 : 7,
              width: 7,
              height: 7,
              borderRadius: 999,
              background: tokens.gold,
            }}
          />
        )}
      </button>
      {open && (
        <div
          style={{
            position: compact ? 'fixed' : 'absolute',
            ...(compact
              ? { left: 8, right: 8, top: 'calc(env(safe-area-inset-top) + 60px)' }
              : { right: 0, top: 'calc(100% + 10px)', width: 360 }),
            zIndex: 120,
            maxHeight: '70vh',
            overflowY: 'auto',
            background: tokens.inkPanel,
            border: `1px solid ${tokens.inkPanelLine}`,
            borderRadius: tokens.radiusMd,
            boxShadow: tokens.shadowDropdown,
            fontFamily: tokens.fontDisplay,
          }}
        >
          <NotificationList
            refreshKey={listKey}
            onSeen={onSeen}
            onClear={onClear}
            onOpen={link => {
              setOpen(false)
              onNavigate(link)
            }}
          />
        </div>
      )}
    </div>
  )
}

/** Mounted only while open, so the list is fetched when someone looks, not on every page. */
function NotificationList({
  refreshKey,
  onSeen,
  onClear,
  onOpen,
}: {
  refreshKey: number
  onSeen: () => Promise<void>
  onClear: () => Promise<void>
  onOpen: (link: string) => void
}) {
  const query = useNotifications(refreshKey)
  const loaded = query.status === 'success'
  useEffect(() => {
    if (loaded) void onSeen()
  }, [loaded, onSeen])

  if (query.status === 'error') {
    return (
      <div style={{ padding: 12 }}>
        <ErrorPanel error={query.error} maxWidth="100%" />
      </div>
    )
  }
  if (query.status !== 'success') return <NotificationListSkeleton />
  if (query.data.length === 0) {
    return (
      <p style={{ margin: 0, padding: '18px 16px', fontSize: 13, color: tokens.inkPanelText }}>
        Nothing yet.
      </p>
    )
  }
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '8px 14px 2px',
          borderBottom: `1px solid ${tokens.inkPanelLine}`,
        }}
      >
        <button
          type="button"
          onClick={() => void onClear()}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '2px 0 6px',
            cursor: 'pointer',
            fontFamily: tokens.fontDisplay,
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tokens.inkPanelText2,
          }}
        >
          Clear
        </button>
      </div>
      <ul aria-label="Notifications" style={{ listStyle: 'none', margin: 0, padding: '6px 0' }}>
        {query.data.map(n => (
          <NotificationRow key={n.id} n={n} onOpen={onOpen} />
        ))}
      </ul>
    </>
  )
}

const MESSAGE_WIDTHS = ['82%', '64%', '90%']

/** Three rows in the list's own shape: dot slot, message, age. Bars are lifted ink, not paper, on the dark panel. */
function NotificationListSkeleton() {
  const bar = { background: tokens.inkPanel2 }
  return (
    <ul aria-busy="true" style={{ listStyle: 'none', margin: 0, padding: '6px 0' }}>
      {MESSAGE_WIDTHS.map((width, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, padding: '9px 14px', alignItems: 'center' }}>
          <span aria-hidden="true" style={{ flex: 'none', width: 7, height: 7 }} />
          <span style={{ flex: 1, minWidth: 0, lineHeight: 1.45, fontSize: 13 }}>
            <SkeletonBar width={width} height={13} delay={i * 80} style={bar} />
          </span>
          <SkeletonBar width={24} height={11} delay={i * 80} style={bar} />
        </li>
      ))}
    </ul>
  )
}

function NotificationRow({ n, onOpen }: { n: AppNotification; onOpen: (link: string) => void }) {
  const body = (
    <>
      <span
        aria-hidden="true"
        style={{
          flex: 'none',
          width: 7,
          height: 7,
          marginTop: 6,
          borderRadius: 999,
          background: n.unread ? tokens.gold : 'transparent',
        }}
      />
      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.45 }}>
        {n.unread && <span style={visuallyHidden}>Unread: </span>}
        {n.message}
      </span>
      <span style={{ flex: 'none', fontSize: 11, color: tokens.inkPanelText2 }}>
        {formatCompactAge(n.createdAt)}
      </span>
    </>
  )
  const row = {
    display: 'flex',
    gap: 10,
    width: '100%',
    padding: '9px 14px',
    textAlign: 'left',
    fontFamily: tokens.fontBody,
    fontSize: 13,
    color: n.unread ? tokens.paper : tokens.inkPanelText3,
  } as const
  return (
    <li>
      {n.link ? (
        <button
          type="button"
          onClick={() => onOpen(n.link!)}
          style={{ ...row, background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          {body}
        </button>
      ) : (
        <div style={row}>{body}</div>
      )}
    </li>
  )
}

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const
